/**
 * Undo / redo for screenshot markup. Immutable: each change pushes the whole mark list,
 * which is tiny, so Clear all and a move undo exactly like a draw.
 */

import type { Mark } from "./marks";

export interface MarkHistory {
  past: Mark[][];
  present: Mark[];
  future: Mark[][];
}

/** Deep enough for any session; the oldest steps fall off. */
const LIMIT = 100;

export function createHistory(initial: Mark[] = []): MarkHistory {
  return { past: [], present: initial, future: [] };
}

/** Record a new state. A change that leaves the marks as they were records nothing. */
export function commit(history: MarkHistory, next: Mark[]): MarkHistory {
  if (next === history.present) return history;
  const past = [...history.past, history.present];
  return {
    past: past.length > LIMIT ? past.slice(past.length - LIMIT) : past,
    present: next,
    future: [],
  };
}

export function undo(history: MarkHistory): MarkHistory {
  const previous = history.past[history.past.length - 1];
  if (previous === undefined) return history;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  };
}

export function redo(history: MarkHistory): MarkHistory {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return { past: [...history.past, history.present], present: next, future: rest };
}

export const canUndo = (history: MarkHistory): boolean => history.past.length > 0;
export const canRedo = (history: MarkHistory): boolean => history.future.length > 0;
